<template>
  <div ref="container">
    <page-banner
      v-if="isLastFromRecurringAppointments"
      dense
      dark
      color="orange-10"
      align="center"
      class="q-mx-sm q-mb-sm"
      content-classes="text-bold"
      label="This is the last appointment in the current recurring rule. To schedule more, edit this appointment and extend the rule."
    />

    <q-list>
      <q-item-wrapper class="text-center">
        <q-item-label
          class="text-body1 text-bold"
          title="Appointment Title"
          :style="{color: readableProfileColor(event.color)}"
        >
          {{ event.title }}
        </q-item-label>

        <div
          v-if="event.types.length > 0"
          class="q-pt-sm"
        >
          <q-chip
            v-for="(d, key) in event.types"
            :key="key"
            :title="d.name"
            dense
            square
            color="grey-3"
            class="appointment-type-chip"
            style="min-width: 100px"
          >
            {{ truncateString(d.name, 35) }}
          </q-chip>
        </div>
      </q-item-wrapper>

      <q-separator spaced inset />

      <template v-if="showActionToolbar">
        <q-tabs
          shrink
          outside-arrows
          mobile-arrows
          :breakpoint="0"
        >
          <stacked-btn
            v-if="showSendMessageAction"
            bubble
            separator
            label="Message"
            icon="mdi-message-draw"
            color="blue-7"
            style="width: 100px;"
            class="full-height"
            @click="$emit('openManualReminder')"
          />
          <template v-if="showQuickActions">
            <stacked-btn
              v-if="can(permissions.canAddQuotations)"
              bubble
              icon="mdi-file-document"
              label="Quotation"
              style="width: 100px;"
              class="full-height"
              @click="$emit('createQuotation')"
            />
            <stacked-btn
              v-if="can(permissions.canLogReports)"
              bubble
              icon="mdi-file-chart"
              label="Report"
              style="width: 100px;"
              class="full-height"
              @click="$emit('createReport')"
            />
            <stacked-btn
              v-if="can(permissions.canAddInvoices)"
              bubble
              icon="mdi-cash"
              label="Invoice"
              style="width: 100px;"
              class="full-height"
              @click="$emit('createInvoice')"
            />
            <stacked-btn
              v-if="can(permissions.canManagePayments)"
              bubble
              icon="mdi-cash-multiple"
              label="Payment"
              color="green-7"
              style="width: 100px;"
              class="full-height"
              @click="$refs.invoiceActions.takePayment()"
            />
          </template>
        </q-tabs>
        <q-separator
          v-if="$q.screen.gt.sm"
          spaced
          inset
        />
      </template>

      <q-list dense class="detail-content">
        <!-- Status -->
        <detail-item
          :name-spacing="detailNameSpacing"
          :with-input="31"
          icon="mdi-bookmark-check"
          name="Status"
        >
          <StatusSelector
            :clickable="isStatusChangeAllowed"
            :label="fieldName"
            :hex-color="fieldColor"
            min-width="130px"
          >
            <StatusPopover
              v-if="isStatusChangeAllowed"
              :id="event.id"
              v-model:status="event.status"
              :can-edit="canEdit"
              :can-send-message="showSendMessageAction"
              :has-clients="event.clients.length > 0"
              :has-all-required-forms="event.has_all_required_forms !== false"
              :has-all-required-checklists="event.has_all_required_checklists !== false"
              with-send-message
              @open-manual-reminder="$emit('openManualReminder')"
              @focus-on-note="goToTimelineNote"
              @send-confirmation-email="sendConfirmationEmail"
            />
          </StatusSelector>
        </detail-item>

        <!-- Invoiced Status (only for completed appointments) -->
        <detail-item
          v-if="isCompletedStatus"
          :name-spacing="detailNameSpacing"
          :with-input="31"
          icon="mdi-currency-usd"
          name="Invoiced Status"
        >
          <StatusSelector
            :clickable="canPerformSimpleActions"
            :label="invoicedStatusLabel"
            :hex-color="invoicedStatusColor"
            min-width="130px"
          >
            <q-popup-proxy v-if="canPerformSimpleActions">
              <q-card style="min-width: 200px;">
                <q-list dense>
                  <q-item-label header class="text-bold">
                    Invoiced Status
                  </q-item-label>
                  <q-item
                    v-close-popup
                    clickable
                    :active="event.is_invoiced_manual === null"
                    @click="setInvoicedStatus(null)"
                  >
                    <q-item-section avatar>
                      <q-icon name="mdi-auto-fix" />
                    </q-item-section>
                    <q-item-section>
                      <q-item-label>Auto</q-item-label>
                      <q-item-label caption>
                        Detect from invoices
                      </q-item-label>
                    </q-item-section>
                  </q-item>
                  <q-item
                    v-close-popup
                    clickable
                    :active="event.is_invoiced_manual === true"
                    @click="setInvoicedStatus(true)"
                  >
                    <q-item-section avatar>
                      <q-icon name="mdi-currency-usd" />
                    </q-item-section>
                    <q-item-section>
                      <q-item-label>Invoiced</q-item-label>
                    </q-item-section>
                  </q-item>
                  <q-item
                    v-close-popup
                    clickable
                    :active="event.is_invoiced_manual === false"
                    @click="setInvoicedStatus(false)"
                  >
                    <q-item-section avatar>
                      <q-icon name="mdi-currency-usd-off" />
                    </q-item-section>
                    <q-item-section>
                      <q-item-label>Not Invoiced</q-item-label>
                    </q-item-section>
                  </q-item>
                </q-list>
              </q-card>
            </q-popup-proxy>
          </StatusSelector>
          <q-chip
            v-if="event.is_invoiced_manual !== null"
            dense
            size="sm"
            color="blue-grey-2"
            class="q-ml-sm"
          >
            Manual
          </q-chip>
        </detail-item>

        <!-- Payment Status (only for completed + invoiced appointments) -->
        <detail-item
          v-if="isCompletedStatus && event.is_invoiced"
          :name-spacing="detailNameSpacing"
          :with-input="31"
          icon="mdi-cash"
          name="Payment Status"
        >
          <StatusSelector
            :clickable="canPerformSimpleActions"
            :label="paidStatusLabel"
            :hex-color="paidStatusColor"
            min-width="130px"
          >
            <q-popup-proxy v-if="canPerformSimpleActions">
              <q-card style="min-width: 200px;">
                <q-list dense>
                  <q-item-label header class="text-bold">
                    Payment Status
                  </q-item-label>
                  <q-item
                    v-close-popup
                    clickable
                    :active="event.is_paid_manual === null"
                    @click="setPaidStatus(null)"
                  >
                    <q-item-section avatar>
                      <q-icon name="mdi-auto-fix" />
                    </q-item-section>
                    <q-item-section>
                      <q-item-label>Auto</q-item-label>
                      <q-item-label caption>
                        Detect from payments
                      </q-item-label>
                    </q-item-section>
                  </q-item>
                  <q-item
                    v-close-popup
                    clickable
                    :active="event.is_paid_manual === true"
                    @click="setPaidStatus(true)"
                  >
                    <q-item-section avatar>
                      <q-icon name="mdi-cash-check" />
                    </q-item-section>
                    <q-item-section>
                      <q-item-label>Paid</q-item-label>
                    </q-item-section>
                  </q-item>
                  <q-item
                    v-close-popup
                    clickable
                    :active="event.is_paid_manual === false"
                    @click="setPaidStatus(false)"
                  >
                    <q-item-section avatar>
                      <q-icon name="mdi-cash-remove" />
                    </q-item-section>
                    <q-item-section>
                      <q-item-label>Not Paid</q-item-label>
                    </q-item-section>
                  </q-item>
                </q-list>
              </q-card>
            </q-popup-proxy>
          </StatusSelector>
          <q-chip
            v-if="event.is_paid_manual !== null"
            dense
            size="sm"
            color="blue-grey-2"
            class="q-ml-sm"
          >
            Manual
          </q-chip>
        </detail-item>

        <!-- Required Templates -->
        <detail-item
          v-if="hasRequiredForms"
          :name-spacing="detailNameSpacing"
          :with-input="35"
          icon="mdi-clipboard-check-outline"
          name="Required Report Templates"
        >
          <div class="row q-gutter-xs required-templates-chips">
            <q-chip
              v-for="form in requiredFormsStatus"
              :key="form.id"
              clickable
              color="white"
              text-color="black"
              style="min-width: 130px"
              class="shadow-2 q-ml-none"
              @click="$emit('startForm', form)"
            >
              {{ form.title }}
              <span
                v-if="form.master_version != null"
                class="text-caption text-grey-7 q-ml-xs"
                style="white-space: nowrap;"
              >(v{{ form.master_version }}.{{ form.minor_version || 0 }})</span>
              <q-icon
                :name="form.isCompleted ? 'mdi-check-circle' : (form.draftReportId ? 'mdi-pencil-circle' : 'mdi-alert-circle')"
                :color="form.isCompleted ? 'green' : (form.draftReportId ? 'blue' : 'orange')"
                size="18px"
                class="q-ml-sm"
              />
            </q-chip>
          </div>
        </detail-item>
        <detail-item
          v-else
          :name-spacing="detailNameSpacing"
          icon="mdi-clipboard-check-outline"
          name="Required Report Templates"
          value="No required templates"
        />

        <!-- Checklists -->
        <detail-item
          v-if="hasChecklists"
          :name-spacing="detailNameSpacing"
          :with-input="35"
          icon="mdi-format-list-checks"
          name="Checklists"
        >
          <q-chip
            clickable
            color="white"
            text-color="black"
            class="shadow-2 q-ml-none"
            style="min-width: 130px"
            @click="$emit('goToChecklists')"
          >
            {{ checklistCompletedCount }}/{{ checklistTotalCount }} completed
            <q-icon
              :name="event.has_all_required_checklists !== false ? 'mdi-check-circle' : 'mdi-alert-circle'"
              :color="event.has_all_required_checklists !== false ? 'green' : 'orange'"
              size="18px"
              class="q-ml-sm"
            />
          </q-chip>
        </detail-item>

        <!-- Date and time -->
        <detail-item
          :name-spacing="detailNameSpacing"
          icon="access_time"
          name="Date"
        >
          <DateTimePartial
            :is-scheduled="event.status !== 'UNSCHEDULED'"
            :all-day="event.all_day"
            :start-date="event.start_date"
            :end-date="event.end_date"
            :timezone="event.timezone_details"
            detailed-timezone
          />
        </detail-item>

        <!-- Address -->
        <detail-item
          v-if="event.address && event.address.full_address"
          :name-spacing="detailNameSpacing"
          icon="mdi-map-marker"
          name="Address"
        >
          <basic-link @click="goToAddress(event.address.full_address)">
            {{ event.address.full_address }}
          </basic-link>
        </detail-item>

        <!-- Description -->
        <detail-item
          v-if="event.description"
          :name-spacing="detailNameSpacing"
          name="Description"
          :value="event.description"
        />

        <!-- Weather -->
        <detail-item
          v-if="event.climate"
          :name-spacing="detailNameSpacing"
          :name="event.climate.is_historical ? 'Weather' : 'Weather Forecast'"
        >
          <template
            v-if="event.climate.icon"
            #icon
          >
            <skycon
              style="margin-left: 2px;margin-top: 2px;"
              :condition="event.climate.icon"
              color="black"
              :width="20"
              :height="20"
            />
          </template>
          <b>{{ event.climate.temperature_high || event.climate.temperature }}°</b> <span v-if="event.climate.summary">- {{ event.climate.summary }}</span>
        </detail-item>

        <!-- Clients -->
        <detail-item
          v-if="event.clients.length > 0"
          :with-input="35"
          :name-spacing="detailNameSpacing"
          :name="`Clients (${event.clients.length})`"
          icon="mdi-account-group-outline"
        >
          <div>
            <client-chip
              v-for="(client, key) in event.clients"
              :key="key"
              :client="client"
              :clickable="!withoutClient"
              :without-btns="['address']"
              min-width="150px"
              class="q-ml-none"
              @click="$emit('viewClient', client.id)"
            />
          </div>
        </detail-item>

        <!-- Staff -->
        <detail-item
          v-if="event.attendees.length > 0"
          :with-input="35"
          :name-spacing="detailNameSpacing"
          :name="`Staff Members (${event.attendees.length})`"
          icon="mdi-account-tie"
        >
          <div>
            <user-chip
              v-for="(user, key) in event.attendees"
              :key="key"
              :user="user"
              clickable
              min-width="150px"
              class="q-ml-none"
              @click="$emit('viewStaff', user.id)"
            />
          </div>
        </detail-item>

        <!-- Timeline -->
        <detail-item
          v-if="can(permissions.canAccessCalendar)"
          ref="timelineContainer"
          icon="mdi-clipboard-file-outline"
          name="Notes & Files"
          sticky
          with-input
          :sticky-top="86"
        >
          <simple-timeline
            ref="timeline"
            :timeline="timeline"
            :read-only="!canPerformSimpleActions"
            :hide-no-entries-text="canPerformSimpleActions"
            disable-add-files
            simple-note
            @add-note="(note, done) => baseAddNote(note, done, event.id, timeline)"
            @update-note="(note, done) => baseUpdateNote(note, done)"
            @remove-note="(entryId, reference) => baseRemoveNote(entryId, reference)"
            @pin-note="(entryId, val, done, reference) => basePinNote(entryId, val, done, reference)"
            @save-attachments="(keepIds, removeObjs, done) => baseSaveAttachments(keepIds, removeObjs, done, event.id, timeline)"
            @remove-attachment="(entryId, key, reference) => baseRemoveAttachment(entryId, key, event.id, reference)"
            @pin-attachment="(entryId, val, key, done, reference) => basePinAttachment(entryId, val, key, done, event.id, reference)"
          >
            <template #buttons>
              <q-btn
                color="blue-7"
                size="12px"
                flat
                dense
                round
                icon="mdi-chart-gantt"
                @click="$emit('goToTimeline')"
              >
                <q-tooltip>View Timeline</q-tooltip>
              </q-btn>
            </template>
          </simple-timeline>
        </detail-item>

        <!-- Reminders -->
        <detail-item
          v-if="showMessagesItem"
          :name-spacing="detailNameSpacing"
          :with-input="!hasReminders ? 31 : undefined"
          name="Automatic Reminders"
          icon="mdi-bell-outline"
          value-classes="q-gutter-sm"
        >
          <q-list v-if="event.client_reminders.length > 0">
            <q-item-label class="text-bold text-blue-grey-9">
              {{ event.client_reminders.length }} client {{ pluraliseString('reminder', event.client_reminders.length) }}
            </q-item-label>

            <ReminderList :reminders="event.client_reminders" />

            <q-item-label
              v-if="event.status === 'UNSCHEDULED' && !company.appointments_send_client_unscheduled_reminders"
              class="text-bold text-red-7 text-caption q-mt-xs"
            >
              Automatic client reminders are disabled for unscheduled appointments
            </q-item-label>
          </q-list>

          <q-list v-if="event.status !== 'UNSCHEDULED' && event.staff_reminders.length > 0">
            <q-item-label class="text-bold text-blue-grey-9">
              {{ event.staff_reminders.length }} scheduled staff {{ pluraliseString('reminder', event.staff_reminders.length) }}
            </q-item-label>
            <ReminderList :reminders="event.staff_reminders" />
          </q-list>

          <q-list v-if="event.status === 'UNSCHEDULED' && event.unscheduled_reminders.length > 0">
            <q-item-label class="text-bold text-blue-grey-9">
              {{ event.unscheduled_reminders.length }} unscheduled staff {{ pluraliseString('reminder', event.unscheduled_reminders.length) }}
            </q-item-label>
            <ReminderList :reminders="event.unscheduled_reminders" />
          </q-list>
        </detail-item>

        <!-- Reference Number -->
        <detail-item
          :name-spacing="detailNameSpacing"
          :value="event.reference_id"
          icon="mdi-book-open-page-variant-outline"
          name="Reference Number"
        />

        <detail-item
          icon="mdi-content-duplicate"
          name="Created On"
        >
          <q-item-label>{{ $formatSystemDate(event.created_at, luxonFormats.FRIENDLY_DATE) }}</q-item-label>
          <q-item-label
            v-if="event.creator"
            caption
          >
            By {{ event.creator.name }}
          </q-item-label>
        </detail-item>

        <!-- Recurring appointments -->
        <q-list v-if="withRecurring && event.is_recurring && event.recurring_appointments.length > 0">
          <q-separator
            inset
            class="q-my-lg"
          />

          <sticky-header static class="q-mt-md">
            Recurring Appointments
          </sticky-header>
          <q-item-label
            caption
            class="q-px-md"
          >
            This appointment is part of a recurring rule.
          </q-item-label>

          <q-list v-if="filteredRecurringList.past.length > 0">
            <sticky-header
              anchor="dialog-menubar"
              color="blue-grey"
              without-padding
            >
              <q-item-label header class="q-py-sm">
                Appointments Beforehand
              </q-item-label>
            </sticky-header>
            <q-virtual-scroll
              :items="filteredRecurringList.past"
              :virtual-scroll-item-size="86"
              scroll-target=".appointment-card"
            >
              <template #default="{ item, index }">
                <appointment-item
                  :appointment="item"
                  clickable
                  @click="viewingAppointmentID = item.id"
                />
              </template>
            </q-virtual-scroll>
          </q-list>

          <q-list v-if="filteredRecurringList.future.length > 0">
            <sticky-header
              anchor="dialog-menubar"
              color="blue-grey"
              without-padding
              class="q-mt-lg"
            >
              <div class="flex q-gutter-sm justify-between">
                <q-item-label header class="q-py-sm">
                  Appointments Afterward
                </q-item-label>
                <q-btn
                  v-if="canEdit && hasFutureDeletableAppointments"
                  size="12px"
                  color="red-8"
                  flat
                  label="Delete Appointments Afterward"
                  icon="mdi-calendar-remove"
                  @click="$emit('deleteUpcoming')"
                />
              </div>
            </sticky-header>
            <q-virtual-scroll
              :items="filteredRecurringList.future"
              :virtual-scroll-item-size="86"
              scroll-target=".appointment-card"
            >
              <template #default="{ item, index }">
                <appointment-item
                  :appointment="item"
                  clickable
                  @click="viewingAppointmentID = item.id"
                />
              </template>
            </q-virtual-scroll>
          </q-list>
        </q-list>
      </q-list>
    </q-list>

    <ViewAppointmentDialog
      v-if="withRecurring"
      :id="viewingAppointmentID"
      :with-recurring="false"
      :with-quick-links="false"
      allow-editing
      allow-deleting
      @close="viewingAppointmentID = null"
    />

    <ConfirmationMessageDialog
      :id="event.id"
      ref="confirmationDialog"
      :clients="event.clients"
    />

    <InvoiceActions
      ref="invoiceActions"
      :appointment-id="event.id"
      @updated="$emit('paid')"
    />
  </div>
</template>

<script>
import { defineAsyncComponent } from 'vue'

import ReminderList from 'components/partials/communications/reminders/ReminderList'
import StatusSelector from '../../../../../partials/StatusSelector'
import StatusPopover from '../../status/Popover'
import DateTimePartial from '../../DateTime'
import ConfirmationMessageDialog from '../../ConfirmationMessageDialog'
import InvoiceActions from './partials/InvoiceActions'

import appointmentTimelineMethodsMixin from 'components/app/calendar/partials/mixins/appointmentTimelineMethodsMixin'
import contextMenuMixin from '../../mixins/contextMenuMixin'
import statusMixin from '../../mixins/statusMixin'

export default {
  components: {
    ReminderList,
    StatusSelector,
    StatusPopover,
    DateTimePartial,
    ConfirmationMessageDialog,
    InvoiceActions,
    ViewAppointmentDialog: defineAsyncComponent(() => import('./../Dialog'))
  },
  mixins: [statusMixin, contextMenuMixin, appointmentTimelineMethodsMixin],
  props: {
    event: {
      type: Object,
      required: true
    },
    timeline: {
      type: Array,
      default: () => {
        return []
      }
    },
    isArchived: {
      type: Boolean,
      default: false
    },
    canEdit: {
      type: Boolean,
      default: false
    },
    canPerformSimpleActions: {
      type: Boolean,
      default: false
    },
    withoutClient: {
      type: Boolean,
      default: false
    },
    withQuickLinks: {
      type: Boolean,
      default: false
    },
    withRecurring: {
      type: Boolean,
      default: true
    }
  },
  emits: ['openManualReminder', 'createQuotation', 'createReport', 'createInvoice', 'paid', 'viewClient', 'viewStaff', 'goToTimeline', 'goToChecklists', 'deleteUpcoming', 'startForm'],
  data () {
    return {
      detailNameSpacing: 150,
      viewingAppointmentID: null
    }
  },
  computed: {
    fieldColor () {
      return this.baseFieldColor(this.event.status)
    },
    fieldName () {
      return this.baseFieldName(this.event.status)
    },
    isCompletedStatus () {
      return this.event.status === 'COMPLETE' || this.event.status === 'DEPARTED_INCOMPLETE'
    },
    invoicedStatusLabel () {
      return this.event.is_invoiced ? 'Invoiced' : 'Not Invoiced'
    },
    invoicedStatusColor () {
      return this.baseEffectiveFieldColor(this.event.status, this.event.is_invoiced)
    },
    paidStatusLabel () {
      return this.event.is_paid ? 'Paid' : 'Not Paid'
    },
    paidStatusColor () {
      return this.baseEffectiveFieldColorWithPayment(this.event.status, this.event.is_invoiced, this.event.is_paid)
    },
    isStatusChangeAllowed () {
      return this.event.status !== 'UNSCHEDULED' && this.canPerformSimpleActions
    },
    filteredRecurringList () {
      const list = {
        past: [],
        future: []
      }

      const thisStartDate = this.luxon.fromSQL(this.event.start_date)

      for (const app of this.event.recurring_appointments) {
        const appStartDate = this.luxon.fromSQL(app.start_date)
        if (appStartDate < thisStartDate) {
          list.past.push(app)
        }
        else if (appStartDate > thisStartDate) {
          list.future.push(app)
        }
      }

      return list
    },
    hasFutureDeletableAppointments () {
      return this.filteredRecurringList.future.filter(item => ['UNSCHEDULED', 'SCHEDULED', 'CANCELLED', 'UNCONFIRMED'].includes(item.status)).length > 0
    },
    isLastFromRecurringAppointments () {
      return this.filteredRecurringList.future.length < 1 && this.filteredRecurringList.past.length > 0
    },
    hasReminders () {
      return this.event.client_reminders.length > 0 ||
        this.event.staff_reminders.length > 0 ||
        (this.event.status === 'UNSCHEDULED' && this.event.unscheduled_reminders.length > 0)
    },
    showMessagesItem () {
      return this.event.clients.length > 0 || this.hasReminders
    },
    showQuickActions () {
      return this.withQuickLinks && this.event.status !== 'UNSCHEDULED' && !this.isArchived && (this.can(this.permissions.canLogReports) || this.can(this.permissions.canAddInvoices) || this.can(this.permissions.canAddQuotations) || this.can(this.permissions.canManagePayments))
    },
    showSendMessageAction () {
      return this.showMessagesItem && this.canPerformSimpleActions && this.event.clients.length > 0
    },
    showActionToolbar () {
      return this.showQuickActions || this.showSendMessageAction
    },
    hasChecklists () {
      return this.event.checklists && this.event.checklists.length > 0
    },
    checklistTotalCount () {
      if (!this.hasChecklists) return 0
      return this.event.checklists.reduce((sum, c) => sum + (c.items ? c.items.length : 0), 0)
    },
    checklistCompletedCount () {
      if (!this.hasChecklists) return 0
      return this.event.checklists.reduce((sum, c) => sum + (c.items ? c.items.filter(i => i.is_completed).length : 0), 0)
    },
    hasRequiredForms () {
      return this.event.required_form_template_ids && this.event.required_form_template_ids.length > 0
    },
    requiredFormsStatus () {
      // Combine required templates with their completion status
      const requiredTemplates = this.event.required_form_templates || []
      const missingIds = (this.event.missing_required_forms || []).map(f => f.id)

      return requiredTemplates.map(template => ({
        id: template.id,
        title: template.title,
        master_version: template.master_version,
        minor_version: template.minor_version,
        isCompleted: !missingIds.includes(template.id),
        draftReportId: template.draft_report_id,
        completedReportId: template.completed_report_id
      }))
    },
    missingFormsCount () {
      return (this.event.missing_required_forms || []).length
    }
  },
  methods: {
    goToTimelineNote () {
      // Focus on note input and scroll to the timeline
      setTimeout(() => {
        this.$refs.timeline.focusOnNewNote()
        this.$refs.container.closest('.content-card').scrollTop = this.$refs.timelineContainer.$el.offsetTop
      }, 250)
    },
    setInvoicedStatus (val) {
      this.baseSetInvoicedStatus(this.event.id, val, (payload) => {
        this.event.is_invoiced = payload.is_invoiced
        this.event.is_invoiced_manual = payload.is_invoiced_manual
      })
    },
    setPaidStatus (val) {
      this.baseSetPaidStatus(this.event.id, val, (payload) => {
        this.event.is_paid = payload.is_paid
        this.event.is_paid_manual = payload.is_paid_manual
      })
    },
    sendConfirmationEmail () {
      this.$refs.confirmationDialog.open()
    }
  }
}
</script>

<style lang="scss" scoped>
.appointment-type-chip :deep(.q-chip__content) {
  justify-content: center;
}

.required-templates-chips :deep(.q-chip) {
  max-width: 100%;
  height: auto;
  min-height: 32px;
  white-space: normal;
}

.required-templates-chips :deep(.q-chip__content) {
  white-space: normal;
  word-break: break-word;
}
</style>
